import RulesConfigurationTable from './components/RulesConfigurationTable'
import {
  Form,
  Grid,
  Layout,
  ToastProvider,
} from '@enterprise-ui/canvas-ui-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import rulesConfigurationService from './services/rulesConfigurationService'
import { ProgressOverlay } from '@dlm/common'
import useUser from '../../common/hooks/useUser'
import apiConfig from '../../config/apiConfig'
import axios from 'axios'

const ALL_CATEGORIES = ''

const RulesConfiguration = () => {
  const makeToast = ToastProvider.useToaster()
  const { accessToken } = useUser()
  const interceptorId = useRef(null)

  // Configure any axios interceptors here
  // Usually we set interceptors globally, but this needs to be inside the component to work with federation
  useEffect(() => {
    interceptorId.current = axios.interceptors.request.use((config) => {
      config.headers['X-API-KEY'] = apiConfig.api.key
      // Usually populated by praxis by default, but doesn't work if accessed from parent mfe app
      config.headers['Authorization'] =
        accessToken && accessToken.includes('Bearer')
          ? accessToken
          : `Bearer ${accessToken}`
      return config
    })

    return () => {
      axios.interceptors.request.eject(interceptorId.current)
    }
  }, [accessToken])

  const [inProgress, setInProgress] = useState(false)
  const [allRuleData, setAllRuleData] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES)

  const getRulesData = useCallback(() => {
    setInProgress(true)
    rulesConfigurationService
      .getRulesData()
      .then((data) => {
        setAllRuleData(data)
      })
      .catch(() => {
        makeToast({
          type: 'error',
          heading: 'Server Error',
          message: 'Error fetching rule data',
        })
        setAllRuleData([])
      })
      .finally(() => {
        setInProgress(false)
      })
  }, [makeToast])

  useEffect(() => {
    getRulesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const categoryOptions = useMemo(() => {
    if (!allRuleData) return []
    const uniqueCategories = [
      ...new Set(allRuleData.map((rule) => rule.rule_category).filter(Boolean)),
    ].sort()
    return [
      { value: ALL_CATEGORIES, label: 'All Categories' },
      ...uniqueCategories.map((cat) => ({
        value: cat,
        label: cat.replace(/_/g, ' '),
      })),
    ]
  }, [allRuleData])

  const filteredRuleData = useMemo(() => {
    if (!allRuleData) return null
    if (!selectedCategory) {
      return allRuleData
    }
    return allRuleData.filter((rule) => rule.rule_category === selectedCategory)
  }, [allRuleData, selectedCategory])

  const ruleCount = filteredRuleData ? filteredRuleData.length : 0

  const tableName = selectedCategory
    ? `${selectedCategory.replace(/_/g, ' ')} Configuration`
    : 'Configuration'

  return (
    <>
      <ProgressOverlay inProgress={inProgress} />
      <Layout.Body data-testid="configuration" includeRail>
        <Grid.Container>
          <Grid.Item xs={3} className="hc-mb-normal">
            <Form.Field
              id="category-filter"
              type="select"
              label="Filter by Category"
              options={categoryOptions}
              value={selectedCategory}
              onUpdate={(id, value) => setSelectedCategory(value)}
              disabled={!allRuleData}
            />
          </Grid.Item>
          <Grid.Item xs={12}>
            <RulesConfigurationTable
              ruleData={filteredRuleData}
              ruleCount={ruleCount}
              onRefresh={getRulesData}
              tableName={tableName}
            />
          </Grid.Item>
        </Grid.Container>
      </Layout.Body>
    </>
  )
}

export default RulesConfiguration
